import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@folio/components';
import { ListFilter, SearchIcon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FilterConfig } from 'korint-sdk/react';
import ValueInput from './ValueInput';
import { isEqual } from 'lodash';
import type { Filter, FilterField } from './types';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export type { Filter, FilterField } from './types';

function isFiltersActive(filters: Filter[]): boolean {
  if (filters.length === 0) return false;

  return filters.some(
    filter =>
      filter.value !== null &&
      filter.value !== undefined &&
      (Array.isArray(filter.value) ? filter.value.length > 0 : true),
  );
}

type SearchAndFiltersProps = {
  search: string;
  setSearch: (value: string) => void;
  filters: Filter[];
  setFilters: (filters: Filter[] | ((prev: Filter[]) => Filter[])) => void;
  filtersConfig: Record<string, FilterConfig>;
  initialFilters?: Filter[];
};

function SearchAndFilters({
  search,
  setSearch,
  filters,
  setFilters,
  filtersConfig,
  initialFilters,
}: SearchAndFiltersProps) {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(
    () => isFiltersActive(filters) && !isEqual(filters, initialFilters),
  );

  const [inputValue, setInputValue] = useState(search);
  const allFields = Object.keys(filtersConfig) as FilterField[];

  // Update input value when search prop changes (e.g., from URL)
  useEffect(() => {
    setInputValue(search);
  }, [search]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(inputValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearch(inputValue);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSearch('');
  };

  const handleFilterFieldChange = (index: number, newField: FilterField) => {
    setFilters(currentFilters =>
      currentFilters.map((filter, i) =>
        i === index
          ? {
              field: newField,
              // Checkbox filters default to `true` when added (an unchecked box is a no-op).
              value: filtersConfig[newField]?.type === 'checkbox' ? true : null,
            }
          : filter,
      ),
    );
  };

  const availableFilterFields = allFields.filter(field => !filters.some(f => f.field === field));

  const areFiltersEnabled = allFields.length > 0;

  return (
    <div className="mb-4">
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="w-full mb-4">
          <Input
            icon={<SearchIcon />}
            placeholder={t('policyTable.searchPlaceholder')}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onClear={handleClear}
            clearable
            aria-describedby="search-help-text"
          />
          <Text size="xs" tone="muted" id="search-help-text" className="mt-1">
            {t('policyTable.searchHelp')}
          </Text>
        </div>
        <Button type="submit" className="mb-4">
          {t('policyTable.searchButton')}
        </Button>
        {areFiltersEnabled && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                size="icon"
                data-testid="filters-button"
              >
                <div className="relative">
                  {isFiltersActive(filters) && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-secondary rounded-full" />
                  )}
                  <ListFilter />
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t(showFilters ? 'policyTable.hideFilters' : 'policyTable.showFilters')}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </form>

      {areFiltersEnabled && showFilters && (
        <div className="p-4 bg-muted/50 border rounded-lg">
          <Stack gap={2}>
            {filters.map((filter, index) => (
              <Group key={`${filter.field}-${index}`} gap={2} className="group">
                <Select
                  value={filter.field}
                  onValueChange={value => handleFilterFieldChange(index, value as FilterField)}
                >
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder={t('policyTable.fieldPlaceholder')}>
                      {t(`filters.${filter.field}.displayName`)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {[...availableFilterFields, filter.field].map(field => (
                      <SelectItem key={field} value={field}>
                        {t(`filters.${field}.displayName`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <ValueInput
                  filter={filter}
                  setFilters={setFilters}
                  index={index}
                  filtersConfig={filtersConfig}
                />

                <Button
                  variant="ghost"
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 transition-[opacity] duration-200 ease-in-out group-hover:duration-0"
                  onClick={() => setFilters(f => f.filter((_, i) => i !== index))}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </Group>
            ))}
            {availableFilterFields.length > 0 && (
              <Button
                className="w-fit"
                variant="ghost"
                size="sm"
                onClick={() =>
                  setFilters(f => [
                    ...f,
                    {
                      field: availableFilterFields[0],
                      value:
                        filtersConfig[availableFilterFields[0]]?.type === 'checkbox' ? true : null,
                    },
                  ])
                }
              >
                + <span className="underline ml-1">{t('policyTable.addCondition')}</span>
              </Button>
            )}
          </Stack>
        </div>
      )}
    </div>
  );
}

export default SearchAndFilters;
